import {
  forwardRef,
  useState,
  useContext,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
// State
import { useWardrobeCard } from "../state/wardrobeCardState";
// Context
import { AlertNotificationContext } from "../contexts/AlertNotificationContext";
import { useSaveStaffOrder } from "../hooks/useSaveStaffOrder";
import { useCheckAPI } from "../hooks/useCheckAPI";
import OrderItemsTable from "./OrderItemsTable";
import { OrderItemsSummaryTable } from "./OrderItemsSummaryTable";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Fab from "@mui/material/Fab";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Slide from "@mui/material/Slide";
import AddShoppingCartIcon from "@mui/icons-material/AddShoppingCart";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Chip from "@mui/material/Chip";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Tooltip from "@mui/material/Tooltip";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutlined";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import TableRowsIcon from "@mui/icons-material/TableRows";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { keyframes } from "@mui/system";
import { ComponentAuthorisation } from "./ComponentAuthorisation";
import { FormProvider, useForm } from "react-hook-form";
import {
  buildInitialDefaults,
  buildLineDefaults,
} from "../utils/variantDefaults";
import { useQueryClient } from "@tanstack/react-query";
import {
  applyBasketChoice,
  findBasketConflicts,
  resolveBackendLineNo,
} from "../utils/basketLines";
import { BasketQuantityDialog } from "./BasketQuantityDialog";

// Pulse keyframes (defined once so emotion generates a class)
const fabPulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(0,0,0,0.25); }
  45% { transform: scale(1.18); box-shadow: 0 0 0 6px rgba(0,0,0,0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(0,0,0,0); }
`;

// Transition
const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const OrderItems = ({
  person,
  order,
  items,
  loading,
  staffRequest,
  itemsCount,
  inlineMode = false,
  orderType = "draft",
  onEditDetails = null,
}) => {
  const { setAlertNotification } = useContext(AlertNotificationContext);
  const [itemsOpen, setItemsOpen] = useState(false);
  const [wardrobeItems, setWardrobeItems] = useState({
    core: [],
    accessory: [],
  });
  const [warningOpen, setWarningOpen] = useState(false);
  const [pendingClose, setPendingClose] = useState(false);
  const allItems = items.core.concat(items.accessory);
  const defaultValues = useMemo(
    () => ({ items: buildInitialDefaults(allItems) }),
    [],
  );
  const formMethods = useForm({ defaultValues, shouldUnregister: false });
  const suppressDefaultsRef = useRef(false);
  const [expanded, setExpanded] = useState(false);
  const handleAccordianChange = useCallback(() => setExpanded((p) => !p), []);
  const [singleColumn, setSingleColumn] = useState(false);
  // MUI breakpoint-based detection (lg and above)
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("lg"));
  const isXs = useMediaQuery(theme.breakpoints.only("xs"));
  // Auto-disable single column view if screen shrinks below lg
  useEffect(() => {
    if (!isLargeScreen && singleColumn) {
      setSingleColumn(false);
    }
  }, [isLargeScreen, singleColumn]);

  // Query client for invalidations
  const queryClient = useQueryClient();

  // Simple effect to add defaults for newly appearing items (replaces truncated complex logic)
  useEffect(() => {
    if (suppressDefaultsRef.current) {
      suppressDefaultsRef.current = false;
      return;
    }
    const current = { ...(formMethods.getValues("items") || {}) };
    const variantSourceCore = wardrobeItems.core.length
      ? wardrobeItems.core
      : items.core;
    const variantSourceAcc = wardrobeItems.accessory.length
      ? wardrobeItems.accessory
      : items.accessory;
    const combined = variantSourceCore.concat(variantSourceAcc);
    let changed = false;
    combined.forEach((prod) => {
      const code = prod.productCode;
      if (!current[code]) {
        // New line entirely
        current[code] = buildLineDefaults(prod);
        changed = true;
        return;
      }
      // Backfill existing empty variant fields if not dirty & enriched data now available
      const candidate = buildLineDefaults(prod);
      const row = current[code];
      ["colour", "size", "fit"].forEach((field) => {
        if (
          row[field] === "" &&
          candidate[field] &&
          !formMethods.getFieldState(`items.${code}.${field}`).isDirty
        ) {
          row[field] = candidate[field];
          changed = true;
        }
      });
    });
    if (changed) {
      formMethods.reset(
        { items: current },
        { keepDirty: false, keepValues: false },
      );
    }
  }, [
    wardrobeItems.core,
    wardrobeItems.accessory,
    items.core,
    items.accessory,
    formMethods,
  ]);

  // ---- Action Bar Handlers ----
  const clearForm = useCallback(() => {
    const current = formMethods.getValues("items") || {};
    const cleared = Object.fromEntries(
      Object.keys(current).map((k) => [
        k,
        { colour: "", size: "", fit: "", quantity: 0 },
      ]),
    );
    suppressDefaultsRef.current = true; // prevent immediate repopulation
    formMethods.reset(
      { items: cleared },
      { keepDirty: false, keepValues: false },
    );
  }, [formMethods]);

  const clearQuantities = useCallback(() => {
    const current = formMethods.getValues("items") || {};
    Object.keys(current).forEach((k) => {
      formMethods.setValue(`items.${k}.quantity`, 0, { shouldDirty: false });
    });
  }, [formMethods]);

  const autofillQuantities = useCallback(() => {
    const variantSourceCore =
      wardrobeItems.core && wardrobeItems.core.length > 0
        ? wardrobeItems.core
        : items.core;
    const variantSourceAcc =
      wardrobeItems.accessory && wardrobeItems.accessory.length > 0
        ? wardrobeItems.accessory
        : items.accessory;
    const sourceAll = variantSourceCore.concat(variantSourceAcc);
    const byCode = Object.fromEntries(sourceAll.map((i) => [i.productCode, i]));
    const current = formMethods.getValues("items") || {};
    Object.entries(current).forEach(([code, row]) => {
      const product = byCode[code];
      if (!product) return;
      const entitlementLeft = Math.max(
        0,
        Number(product.entitlement) - Number(product.qtyIssued),
      );
      // Only set if SKU currently valid (all required variant dims chosen)
      const needsSize = !!product.sizes?.length;
      const needsFit = !!product.fits?.length;
      const skuValid =
        (!!row.colour || !product.colours) &&
        (!needsSize || row.size) &&
        (!needsFit || row.fit);
      formMethods.setValue(
        `items.${code}.quantity`,
        skuValid ? entitlementLeft : 0,
        { shouldDirty: false },
      );
    });
  }, [
    formMethods,
    wardrobeItems.core,
    wardrobeItems.accessory,
    items.core,
    items.accessory,
  ]);

  const resetToDefaults = useCallback(() => {
    const variantSourceCore =
      wardrobeItems.core && wardrobeItems.core.length > 0
        ? wardrobeItems.core
        : items.core;
    const variantSourceAcc =
      wardrobeItems.accessory && wardrobeItems.accessory.length > 0
        ? wardrobeItems.accessory
        : items.accessory;
    const combined = variantSourceCore.concat(variantSourceAcc);
    const defaultsMap = buildInitialDefaults(combined);
    formMethods.reset(
      { items: defaultsMap },
      { keepDirty: false, keepValues: false },
    );
  }, [
    formMethods,
    wardrobeItems.core,
    wardrobeItems.accessory,
    items.core,
    items.accessory,
  ]);

  // Bulk Add All handler
  const { mutate: bulkSave, isPending: bulkSaving } = useSaveStaffOrder();
  const [addAllPending, setAddAllPending] = useState(null);

  // Every existing draft line for this staff member (blank placeholders and filled variants)
  const basketLines = useMemo(
    () => items.core.concat(items.accessory),
    [items.core, items.accessory],
  );

  // Map productCode -> backend line number (replicates row-level fallback logic)
  const backendLineNoMap = useMemo(() => {
    const map = {};
    try {
      wardrobeItems.core.forEach((wItem, idx) => {
        const existing = items.core.find(
          (o) => o.productCode === wItem.productCode,
        );
        map[wItem.productCode] = existing?.lineNo ?? idx;
      });
      wardrobeItems.accessory.forEach((wItem, idx) => {
        const existing = items.accessory.find(
          (o) => o.productCode === wItem.productCode,
        );
        map[wItem.productCode] = existing?.lineNo ?? idx; // no offset to mirror single add behaviour
      });
    } catch (e) {
      // silent; map stays partial
    }
    return map;
  }, [
    wardrobeItems.core,
    wardrobeItems.accessory,
    items.core,
    items.accessory,
  ]);
  const submitAllLines = useCallback(
    (lines) => {
      bulkSave(
        {
          data: {
            filter: { orderNo: order.no, staffLineNo: person.staffLineNo },
            lines,
            reason: person.reasonCode,
            staffpoNo: person.staffpoNo,
          },
        },
        {
          onSuccess: () => {
            setAlertNotification({
              message: `${lines.length} item(s) added`,
              severity: "success",
            });
            // Clear quantities of added lines so user sees they were processed
            lines.forEach((l) =>
              formMethods.setValue(`items.${l.productCode}.quantity`, 0, {
                shouldDirty: false,
              }),
            );
            // Invalidate queries to refresh order + wardrobe state (parity with single add)
            queryClient.invalidateQueries({
              queryKey: ["draftOrderStaff", order.no, person.staffLineNo],
            });
            queryClient.invalidateQueries({
              queryKey: ["wardrobeCard", person.uniformId, person.no],
            });
          },
          onError: () => {
            setAlertNotification({
              message: "Bulk add failed",
              severity: "error",
            });
          },
        },
      );
    },
    [
      bulkSave,
      formMethods,
      order.no,
      person.staffLineNo,
      person.reasonCode,
      person.staffpoNo,
      person.uniformId,
      person.no,
      setAlertNotification,
      queryClient,
    ],
  );
  const handleAddAll = useCallback(() => {
    const variantSourceCore =
      wardrobeItems.core && wardrobeItems.core.length > 0
        ? wardrobeItems.core
        : items.core;
    const variantSourceAcc =
      wardrobeItems.accessory && wardrobeItems.accessory.length > 0
        ? wardrobeItems.accessory
        : items.accessory;
    const sourceAll = variantSourceCore.concat(variantSourceAcc);
    const byCode = Object.fromEntries(sourceAll.map((i) => [i.productCode, i]));
    const formItems = formMethods.getValues("items") || {};

    const lines = Object.entries(formItems)
      .map(([code, row]) => {
        const product = byCode[code];
        if (!product) return null;
        if (!row || !row.quantity || Number(row.quantity) <= 0) return null;
        const needsColour = !!product.colours?.length;
        const needsSize = !!product.sizes?.length;
        const needsFit = !!product.fits?.length;
        const skuValid =
          (!needsColour || row.colour) &&
          (!needsSize || row.size) &&
          (!needsFit || row.fit);
        if (!skuValid) return null;
        const variant = {
          productCode: code,
          colour: row.colour || null,
          size: row.size || null,
          fit: row.fit || null,
        };
        return {
          lineNo:
            resolveBackendLineNo(
              basketLines,
              variant,
              backendLineNoMap[code],
            ) ?? null,
          ...variant,
          quantity: Number(row.quantity),
          overEntitlementReason: null,
        };
      })
      .filter(Boolean);

    if (lines.length === 0) {
      setAlertNotification({
        message: "No valid lines to add",
        severity: "info",
      });
      return;
    }

    // Bulk lists can repeat a variant, so ask whether to add or overwrite
    if (orderType === "bulk") {
      const conflicts = findBasketConflicts(basketLines, lines);
      if (conflicts.length > 0) {
        setAddAllPending({ lines, conflicts });
        return;
      }
    }

    submitAllLines(lines);
  }, [
    formMethods,
    wardrobeItems.core,
    wardrobeItems.accessory,
    backendLineNoMap,
    basketLines,
    items.core,
    items.accessory,
    orderType,
    setAlertNotification,
    submitAllLines,
  ]);

  const handleAddAllChoice = useCallback(
    (choice) => {
      if (!addAllPending) return;
      const { lines, conflicts } = addAllPending;
      setAddAllPending(null);
      submitAllLines(applyBasketChoice(lines, conflicts, choice));
    },
    [addAllPending, submitAllLines],
  );

  // Helper: check for un-added items (quantity > 0 and not in items.selected)
  const hasUnaddedItems = useCallback(() => {
    const formItems = formMethods.getValues("items");
    const addedProductCodes = (items.selected || []).map(
      (i) => i.productCode + ":" + i.quantity,
    );
    return Object.entries(formItems).some(([lineNo, row]) => {
      if (!row || !row.quantity || Number(row.quantity) === 0) return false;
      const wardrobeItem = allItems.find(
        (item) => String(item.lineNo) === String(lineNo),
      );
      if (!wardrobeItem) return false;
      const key = wardrobeItem.productCode + ":" + row.quantity;
      return !addedProductCodes.includes(key);
    });
  }, [formMethods, items, allItems]);

  const handleClickOpen = useCallback(() => {
    setItemsOpen(true);
  }, []);

  const handleCloseWithCheck = useCallback(() => {
    if (hasUnaddedItems()) {
      setWarningOpen(true);
      setPendingClose(true);
    } else {
      setItemsOpen(false);
    }
  }, [hasUnaddedItems]);

  const handleWarningContinue = () => {
    setWarningOpen(false);
    setItemsOpen(false);
    setPendingClose(false);
  };

  const handleWarningStay = () => {
    setWarningOpen(false);
    setPendingClose(false);
  };

  // Fetch wardrobe details
  const { isSuccess, data, isPending, isError, error } = useWardrobeCard(
    person.uniformId,
    person.no,
    !loading,
  );

  // Success Function
  const successFunc = useCallback(() => {
    const items = data.data.data.data.data.lines;
    if (!items) return;
    const coreItems = items.filter((item) => item.type === "Core");
    const accessoryItems = items.filter((item) => item.type === "Accessory");
    setWardrobeItems({
      core: coreItems,
      accessory: accessoryItems,
    });
  }, [data]);

  // Error Function
  const errorFunc = useCallback(() => {
    setAlertNotification({
      message: `Error: ${data.data.status}: ${data.data.statusText}`,
      severity: "error",
    });
  }, [data]);

  // Hook to check the BC API for errors
  useCheckAPI(isSuccess, data, data?.data?.status, successFunc, errorFunc);

  // item list content (placed after hooks so dependencies defined)
  const itemListContent = (
    <FormProvider {...formMethods}>
      <Box sx={{ m: 1, my: 2, mx: { xs: 0, md: 1 } }}>
        <Accordion expanded={expanded} onChange={handleAccordianChange}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1bh-content"
            id="panel1bh-header"
            sx={{
              "& .MuiAccordionSummary-content": {
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                alignItems: "center",
                width: "100%",
              },
            }}
          >
            <Typography variant="subtitle1" sx={{ mr: 1 }}>
              Summary - {person.name}
            </Typography>
            <Chip
              label={itemsCount}
              color="primary"
              size="small"
              variant="outlined"
              sx={{ my: "auto" }}
            />
            {/* Action buttons moved from toolbar */}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1,
                ml: "auto",
                mr: 2,
              }}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === " " || e.key === "Enter") {
                  e.stopPropagation();
                }
              }}
            >
              {isLargeScreen && (
                <Tooltip
                  title={
                    singleColumn
                      ? "Switch to multi column layout"
                      : "Switch to single column layout"
                  }
                >
                  <span>
                    <IconButton
                      size="small"
                      color="primary"
                      aria-label={
                        singleColumn
                          ? "switch to multi column layout"
                          : "switch to single column layout"
                      }
                      onClick={() => setSingleColumn((p) => !p)}
                    >
                      {singleColumn ? <ViewColumnIcon /> : <TableRowsIcon />}
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              {onEditDetails && (
                <Tooltip title="Edit staff details.">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={onEditDetails}
                    >
                      Edit Details
                    </Button>
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Restore starting choices and zero all quantities.">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    startIcon={isXs ? null : <RestartAltIcon />}
                    onClick={resetToDefaults}
                  >
                    Reset
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title="Remove all choices and quantities.">
                <span>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    startIcon={isXs ? null : <ClearAllIcon />}
                    onClick={clearForm}
                  >
                    Clear
                  </Button>
                </span>
              </Tooltip>
              <ComponentAuthorisation allowedRoles={["allowentitlement"]}>
                <Tooltip title="Zero quantities; keep your chosen options.">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      color="info"
                      startIcon={isXs ? null : <RemoveCircleOutlineIcon />}
                      onClick={clearQuantities}
                    >
                      Zero QTY
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Fill each valid item with the remaining entitlement.">
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={isXs ? null : <AutoAwesomeIcon />}
                      onClick={autofillQuantities}
                    >
                      Autofill
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip title="Add every item with a quantity to the order.">
                  <span>
                    <Button
                      size="small"
                      variant="contained"
                      color="success"
                      startIcon={isXs ? null : <PlaylistAddIcon />}
                      disabled={bulkSaving}
                      onClick={handleAddAll}
                    >
                      Add All
                    </Button>
                  </span>
                </Tooltip>
              </ComponentAuthorisation>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <div>
              <OrderItemsSummaryTable
                items={items.selected}
                order={order}
                person={person}
                staffRequest={staffRequest}
                orderType={orderType}
              />
            </div>
          </AccordionDetails>
        </Accordion>
      </Box>
      {isSuccess && (
        <Stack spacing={2} sx={{ m: 1, mt: 0, mx: { xs: 0, md: 1 } }}>
          {/* Toolbar removed; actions now inside accordion summary */}
          <OrderItemsTable
            type="Core"
            heading={`Core Items - ${person.uniform}`}
            wardrobeItems={wardrobeItems.core}
            wardrobeLoading={isPending}
            items={items.core}
            order={order}
            person={person}
            staffRequest={staffRequest}
            backendLineNoMap={backendLineNoMap}
            basketLines={basketLines}
            control={formMethods.control}
            setValue={formMethods.setValue}
            getFieldState={formMethods.getFieldState}
            clearErrors={formMethods.clearErrors}
            watch={formMethods.watch}
            handleSubmit={formMethods.handleSubmit}
            resetField={formMethods.resetField}
            singleColumn={singleColumn}
            orderType={orderType}
          />
          {wardrobeItems.accessory.length !== 0 && !staffRequest && (
            <OrderItemsTable
              type="Accessory"
              heading={`Accessory Items - ${person.uniform}`}
              wardrobeItems={wardrobeItems.accessory}
              wardrobeLoading={isPending}
              items={items.accessory}
              order={order}
              person={person}
              staffRequest={staffRequest}
              backendLineNoMap={backendLineNoMap}
              basketLines={basketLines}
              control={formMethods.control}
              setValue={formMethods.setValue}
              getFieldState={formMethods.getFieldState}
              clearErrors={formMethods.clearErrors}
              watch={formMethods.watch}
              handleSubmit={formMethods.handleSubmit}
              resetField={formMethods.resetField}
              singleColumn={singleColumn}
              orderType={orderType}
            />
          )}
        </Stack>
      )}
      <BasketQuantityDialog
        open={!!addAllPending}
        conflicts={addAllPending?.conflicts ?? []}
        onChoose={handleAddAllChoice}
        onCancel={() => setAddAllPending(null)}
      />
    </FormProvider>
  );

  // Remove obsolete ItemList component definition (now inlined)

  return (
    <>
      {staffRequest || inlineMode ? (
        itemListContent
      ) : (
        <>
          <Tooltip title={`Add items to the order for ${person.name}`}>
            <Fab
              size="small"
              aria-label={`add items to the order for ${person.name}`}
              onClick={handleClickOpen}
              sx={{
                bgcolor: (theme) => theme.palette.success.main,
                color: (theme) => theme.palette.success.contrastText,
                "&:hover": {
                  bgcolor: (theme) => theme.palette.success.dark,
                },
                boxShadow: 4,
              }}
            >
              <AddShoppingCartIcon />
            </Fab>
          </Tooltip>
          <Dialog
            fullScreen
            open={itemsOpen}
            onClose={handleCloseWithCheck}
            slots={{
              transition: Transition,
            }}
          >
            <AppBar sx={{ position: "relative" }}>
              <Toolbar>
                <Typography
                  sx={{ ml: 2, flex: 1 }}
                  variant="h6"
                  component="div"
                >
                  {person.name} - {person.yourId}
                </Typography>
                <Button
                  autoFocus
                  color="inherit"
                  onClick={handleCloseWithCheck}
                >
                  save
                </Button>
              </Toolbar>
            </AppBar>
            {itemListContent}
            {/* Warning dialog for un-added items */}
            <Dialog open={warningOpen} onClose={handleWarningStay}>
              <DialogTitle>Items Not Yet Added</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  You have started entering one or more items, but they have not
                  yet been added to the order. If you close now, any changes to
                  these items will be lost.
                  <br />
                  <br />
                  Would you like to continue and close the order form, or stay
                  and finish adding your items?
                </DialogContentText>
              </DialogContent>
              <Stack
                direction="row"
                spacing={2}
                sx={{ p: 2, justifyContent: "flex-end" }}
              >
                <Button
                  onClick={handleWarningStay}
                  color="primary"
                  variant="outlined"
                >
                  Stay
                </Button>
                <Button
                  onClick={handleWarningContinue}
                  color="error"
                  variant="contained"
                >
                  Continue
                </Button>
              </Stack>
            </Dialog>
          </Dialog>
        </>
      )}
    </>
  );
};
